/**
 * Fixed Gemini AI models and their cost configurations (input, output, thinking cost per million tokens).
 */
export const GEMINI_MODEL_CONFIGS = {
    "gemini-3.8-flash": {
        inputCostPerMillionTokens: 0.75,
        outputCostPerMillionTokens: 3.75,
        thinkingCostPerMillionTokens: 3.75,
    },
    "gemini-3.1-flash-lite": {
        inputCostPerMillionTokens: 0.25,
        outputCostPerMillionTokens: 1.5,
        thinkingCostPerMillionTokens: 1.5,
    },
    "gemini-3.1-flash-lite-preview": {
        inputCostPerMillionTokens: 0.25,
        outputCostPerMillionTokens: 1.5,
        thinkingCostPerMillionTokens: 1.5,
    },
    "gemini-2.5-flash": {
        inputCostPerMillionTokens: 0.3,
        outputCostPerMillionTokens: 2.5,
        thinkingCostPerMillionTokens: 2.5,
    },
    "gemini-3-flash-preview": {
        inputCostPerMillionTokens: 0.5,
        outputCostPerMillionTokens: 3.0,
        thinkingCostPerMillionTokens: 3.0,
    },
    "gemini-2.5-pro": {
        inputCostPerMillionTokens: 1.25,
        outputCostPerMillionTokens: 10.0,
        thinkingCostPerMillionTokens: 10.0,
    },
    "gemini-2.0-flash": {
        inputCostPerMillionTokens: 0.1,
        outputCostPerMillionTokens: 0.4,
        thinkingCostPerMillionTokens: 0.4,
    },
    "gemini-2.0-flash-lite": {
        inputCostPerMillionTokens: 0.075,
        outputCostPerMillionTokens: 0.3,
        thinkingCostPerMillionTokens: 0.3,
    },
    "gemini-1.5-flash": {
        inputCostPerMillionTokens: 0.075,
        outputCostPerMillionTokens: 0.3,
        thinkingCostPerMillionTokens: 0.3,
    },
    "gemini-1.5-pro": {
        inputCostPerMillionTokens: 1.25,
        outputCostPerMillionTokens: 5.0,
        thinkingCostPerMillionTokens: 5.0,
    },
    "gemini-3-pro-preview": {
        inputCostPerMillionTokens: 1.25,
        outputCostPerMillionTokens: 10.0,
        thinkingCostPerMillionTokens: 10.0,
    },
};

export const OPENAI_MODEL_CONFIGS = {
    "gpt-6-luna": {
        inputCostPerMillionTokens: 0.1,
        outputCostPerMillionTokens: 0.5,
        thinkingCostPerMillionTokens: 0.5,
    },
};

export const DEFAULT_AI_PROVIDERS = [
    { value: "gemini", label: "Gemini" },
    { value: "openai", label: "OpenAI" },
];

export const DEFAULT_GEMINI_MODELS = Object.keys(GEMINI_MODEL_CONFIGS).map((model) => ({
    value: model,
    label: model,
}));

export const DEFAULT_OPENAI_MODELS = Object.keys(OPENAI_MODEL_CONFIGS).map((model) => ({
    value: model,
    label: model,
}));

export const DEFAULT_AI_MODELS = [
    ...DEFAULT_GEMINI_MODELS,
    ...DEFAULT_OPENAI_MODELS,
];

/**
 * Lookup config for any model and optional provider
 */
export const getModelPreset = (modelName, provider) => {
    if (!modelName || typeof modelName !== "string") return null;
    const cleanName = modelName.trim().toLowerCase();
    const normalizedName = cleanName.replace(/\s+/g, "-");

    const matchModelKey = (configs) =>
        Object.keys(configs).find((key) => {
            const k = key.toLowerCase();
            return k === cleanName || k === normalizedName;
        });

    if (provider && provider.toLowerCase() === "openai") {
        const matchKey = matchModelKey(OPENAI_MODEL_CONFIGS);
        if (matchKey) return OPENAI_MODEL_CONFIGS[matchKey];
    }

    if (provider && provider.toLowerCase() === "gemini") {
        const matchKey = matchModelKey(GEMINI_MODEL_CONFIGS);
        if (matchKey) return GEMINI_MODEL_CONFIGS[matchKey];
    }

    const geminiMatch = matchModelKey(GEMINI_MODEL_CONFIGS);
    if (geminiMatch) return GEMINI_MODEL_CONFIGS[geminiMatch];

    const openAiMatch = matchModelKey(OPENAI_MODEL_CONFIGS);
    return openAiMatch ? OPENAI_MODEL_CONFIGS[openAiMatch] : null;
};

/**
 * Lookup config for a model name (case-insensitive) - backward compatible
 */
export const getGeminiModelConfig = (modelName) => {
    return getModelPreset(modelName, "gemini");
};

